import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Input,
  Button,
  Grid,
  Snackbar,
  CssBaseline,
  Select,
  Option,
  IconButton,
  Chip,
  Tooltip,
  Breadcrumbs,
  Modal,
  ModalDialog,
  ModalClose,
  FormControl,
  FormLabel,
  Stack,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/joy';
import {
  Search as SearchIcon,
  KeyboardArrowRight,
  Edit as EditIcon,
  Delete as DeleteIcon,
  WarningRounded,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Undo as UndoIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material';
import Link from '@mui/joy/Link';
import { DataGrid } from '@mui/x-data-grid';
import { dataGridNativeCheckboxSlots } from '../../main/components/dataGridNativeCheckboxSlots';
import { getPayments, updatePayment, deletePayment, confirmPayment, bulkConfirmPayments, refundPayment } from '../../main/API/payment';
import PermissionGate from '../../main/components/PermissionGate';
import { PERMISSIONS } from '../../main/context/AuthContext';
import { usePermissions } from '../../main/hooks/usePermissions';
import { DirectionalMaterialThemeProvider } from '../../main/theme/DirectionalMaterialThemeProvider';
import AccountOptionList from '../../main/widgets/AccountOptionList';
import CustomerOptionList from '../../main/widgets/CustomersOptionList';
import { PAYMENT_METHODS, PAYMENT_METHOD_OPTIONS, PAYMENT_METHOD_OPTIONS_WITH_ALL } from '../../main/constants/paymentMethods';
import PaymentReceipt from '../../shipments_manager/views/payment_receipt_screen';
import { OmrAmount } from '../../main/components/OmrAmount';
import { translateApiErrorData, translateAxiosErrorMessage, translateAggregateBackendMessage } from '../../main/utils/masterApiError';
import { getLocalizedUserFullName } from '../../main/utils/userDisplayName';

export default function PaymentsScreen() {
  const { t, i18n } = useTranslation('ui');
  const { hasPermission } = usePermissions();

  const paymentMethodLabel = useCallback(
    (value) => {
      if (value === '' || value == null) {
        return t('widgets.paymentMethodLabels.all');
      }
      return t(`widgets.paymentMethodLabels.${value}`, {
        defaultValue: PAYMENT_METHODS[value] || String(value),
      });
    },
    [t],
  );
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [data, setData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });
  const [filters, setFilters] = useState({
    search: '',
    customer: '',
    payment_method: '',
    account: '',
    start_date: '',
    end_date: '',
    is_confirmed: '',
    is_refunded: '',
    confirmed_at_start: '',
    confirmed_at_end: '',
  });

  // Selected account for filter (used with AccountOptionList)
  const [filterAccount, setFilterAccount] = useState(null);
  const [filterCustomer, setFilterCustomer] = useState(null);
  
  // Selection state for bulk operations
  const [selectionModel, setSelectionModel] = useState([]);
  
  // Notification states
  const [failedNotification, setFailedNotification] = useState(false);
  const [successNotification, setSuccessNotification] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');

  // Edit modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editForm, setEditForm] = useState({
    payment_method: '',
    amount: '',
    transaction_reference: '',
    account: null,
  });
  const [editFormErrors, setEditFormErrors] = useState({
    amount: '',
  });

  // Filter validation errors
  const [filterErrors, setFilterErrors] = useState({
    start_date: '',
    end_date: '',
    confirmed_at_start: '',
    confirmed_at_end: '',
  });

  // Delete modal states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingPayment, setDeletingPayment] = useState(null);

  // Refund modal states
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [refundingPayment, setRefundingPayment] = useState(null);
  const [refundReason, setRefundReason] = useState('');

  const [receiptPdfPayment, setReceiptPdfPayment] = useState(null);

  // Validation function
  const validatePaymentAmount = (amount) => {
    const numAmount = parseFloat(amount);
    
    if (isNaN(numAmount)) {
      return { valid: false, error: t('paymentsScreen.errAmountNumber') };
    }
    
    if (numAmount <= 0) {
      return { valid: false, error: t('paymentsScreen.errAmountPositive') };
    }
    
    return { valid: true };
  };

  const loadPayments = useCallback(async () => {
    if (!hasPermission(PERMISSIONS.PAYMENTS_LIST)) {
      setPayments([]);
      return;
    }

    setLoading(true);
    try {
      // Build params object with proper API parameter names
      const params = {
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
        pagination: true,
      };
      
      // Add filters if they have values
      if (filters.search) params.search = filters.search;
      if (filters.customer) params.customer = filters.customer;
      if (filters.payment_method) params.payment_method = filters.payment_method;
      if (filters.account) params.account = filters.account;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.is_confirmed !== '') params.is_confirmed = filters.is_confirmed;
      if (filters.is_refunded !== '') params.is_refunded = filters.is_refunded;
      // Convert date to datetime format for confirmed_at filters
      if (filters.confirmed_at_start) {
        // Add time component for start of day (00:00:00)
        params.confirmed_at_start = `${filters.confirmed_at_start}T00:00:00`;
      }
      if (filters.confirmed_at_end) {
        // Add time component for end of day (23:59:59)
        params.confirmed_at_end = `${filters.confirmed_at_end}T23:59:59`;
      }
      
      const response = await getPayments(params);
      if (response && response.data) {
        setData(response.data);
        setPayments(response.data.results || []);
      } else {
        setPayments([]);
        setData(null);
      }
    } catch (error) {
      console.error('Failed to load payments:', error);
      setResponseMessage(error.message || t('paymentsScreen.loadFailed'));
      setFailedNotification(true);
      setPayments([]);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [hasPermission, paginationModel.page, paginationModel.pageSize, filters, t]);

  useEffect(() => {
    loadPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setFilterErrors({
      start_date: '',
      end_date: '',
      confirmed_at_start: '',
      confirmed_at_end: '',
    });
    
    // Validate created_at date range
    if (filters.start_date && filters.end_date) {
      const startDate = new Date(filters.start_date);
      const endDate = new Date(filters.end_date);
      if (startDate > endDate) {
        setFilterErrors({
          start_date: 'error',
          end_date: 'error',
          confirmed_at_start: '',
          confirmed_at_end: '',
        });
        setResponseMessage(t('paymentsScreen.errCreatedRange'));
        setFailedNotification(true);
        return;
      }
    }
    
    // Validate confirmed_at date range
    if (filters.confirmed_at_start && filters.confirmed_at_end) {
      const confirmedStartDate = new Date(filters.confirmed_at_start);
      const confirmedEndDate = new Date(filters.confirmed_at_end);
      if (confirmedStartDate > confirmedEndDate) {
        setFilterErrors({
          start_date: '',
          end_date: '',
          confirmed_at_start: 'error',
          confirmed_at_end: 'error',
        });
        setResponseMessage(t('paymentsScreen.errConfirmedRange'));
        setFailedNotification(true);
        return;
      }
    }
    
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    loadPayments();
  };

  const handlePageSizeChange = (pageSize) => {
    setPaginationModel({ page: 0, pageSize });
  };

  const handlePageChange = (page) => {
    setPaginationModel((prev) => ({ ...prev, page }));
  };

  // Edit handlers
  const handleEditClick = (payment) => {
    setEditingPayment(payment);
    // Create account object if account ID exists
    const accountObj = payment.account ? {
      id: payment.account,
      name: payment.account_name || '',
    } : null;
    
    setEditForm({
      payment_method: payment.payment_method || '',
      amount: payment.amount || '',
      transaction_reference: payment.transaction_reference || '',
      account: accountObj,
    });
    setEditFormErrors({ amount: '' });
    setEditModalOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!editingPayment) return;
    if (!hasPermission(PERMISSIONS.PAYMENTS_UPDATE)) return;

    // Clear previous errors
    setEditFormErrors({ amount: '' });

    // Validate amount before API call
    const amountValidation = validatePaymentAmount(editForm.amount);
    if (!amountValidation.valid) {
      setEditFormErrors({ amount: amountValidation.error });
      return;
    }

    setLoading(true);
    try {
      // Prepare payload - extract account ID if account object exists
      const payload = {
        payment_method: editForm.payment_method,
        amount: editForm.amount,
        transaction_reference: editForm.transaction_reference,
      };
      
      // Add account ID if account is selected
      if (editForm.account && editForm.account.id) {
        payload.account = editForm.account.id;
      }
      
      await updatePayment(editingPayment.id, payload);
      
      // Success - axios throws errors for 400+ status codes, so if we reach here, it's successful
      setResponseMessage(t('paymentsScreen.updatedSuccess'));
      setSuccessNotification(true);
      setEditModalOpen(false);
      setEditingPayment(null);
      loadPayments();
    } catch (error) {
      console.error('Failed to update payment:', error);
      
      if (error.response && error.response.status === 400) {
        const errorData = error.response.data;
        const fallback = t('paymentsScreen.updateFailed');
        if (errorData && errorData.amount) {
          const amountError = Array.isArray(errorData.amount) ? errorData.amount[0] : errorData.amount;
          setEditFormErrors({ amount: translateAggregateBackendMessage(String(amountError), t) });
        }
        setResponseMessage(translateApiErrorData(errorData, t) || fallback);
        setFailedNotification(true);
      } else {
        setResponseMessage(translateAxiosErrorMessage(error, t) || t('paymentsScreen.updateFailed'));
        setFailedNotification(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // Delete handlers
  const handleDeleteClick = (payment) => {
    setDeletingPayment(payment);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingPayment) return;
    if (!hasPermission(PERMISSIONS.PAYMENTS_DELETE)) return;
    setLoading(true);
    try {
      await deletePayment(deletingPayment.id);
      
      // Success - axios returns response with status 200-299 for success
      setResponseMessage(t('paymentsScreen.deletedSuccess'));
      setSuccessNotification(true);
      setDeleteModalOpen(false);
      setDeletingPayment(null);
      loadPayments();
    } catch (error) {
      console.error('Failed to delete payment:', error);
      setResponseMessage(translateAxiosErrorMessage(error, t) || t('paymentsScreen.deleteFailed'));
      setFailedNotification(true);
    } finally {
      setLoading(false);
    }
  };

  // Extract user-friendly error message from refund API response (including nested details.response)
  const extractRefundErrorMessage = (data) => {
    if (!data || typeof data !== 'object') return t('paymentsScreen.refundFailed');
    let msg = data.message || data.detail || data.error || '';
    const details = data.details;
    if (details && typeof details === 'object' && details.response) {
      try {
        const parsed = typeof details.response === 'string' ? JSON.parse(details.response) : details.response;
        const nestedMsg = parsed && (parsed.message || parsed.detail || parsed.error);
        if (nestedMsg) msg = nestedMsg;
      } catch (_) {
        if (typeof details.response === 'string') msg = msg || details.response;
      }
    }
    return msg || t('paymentsScreen.refundFailed');
  };

  // Refund handlers (always full refund)
  const handleRefundClick = (payment) => {
    setRefundingPayment(payment);
    setRefundReason('');
    setRefundModalOpen(true);
  };

  const handleRefundConfirm = async () => {
    if (!refundingPayment) return;

    if (!hasPermission(PERMISSIONS.PAYMENTS_REFUND)) {
      setResponseMessage(t('paymentsScreen.noRefundPermission'));
      setFailedNotification(true);
      return;
    }

    setLoading(true);
    try {
      const options = {};
      if (refundReason.trim()) options.reason = refundReason.trim();
      const response = await refundPayment(refundingPayment.id, options);
      const data = response && response.data;

      if (data && data.status === 'error') {
        const errorMessage = translateAggregateBackendMessage(extractRefundErrorMessage(data), t);
        setSuccessNotification(false);
        setResponseMessage(errorMessage);
        setFailedNotification(true);
        return;
      }

      setResponseMessage(t('paymentsScreen.refundSuccess'));
      setSuccessNotification(true);
      setRefundModalOpen(false);
      setRefundingPayment(null);
      setRefundReason('');
      loadPayments();
    } catch (error) {
      console.error('Failed to refund payment:', error);
      const errorData = error.response && error.response.data;
      const errorMessage = translateAggregateBackendMessage(
        extractRefundErrorMessage(errorData || { message: error.message }),
        t
      );
      setSuccessNotification(false);
      setResponseMessage(errorMessage);
      setFailedNotification(true);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = useCallback((dateString) => {
    if (!dateString) return '-';
    try {
      const d = new Date(dateString);
      const time = d.toLocaleTimeString('en-US', { timeZone: 'Asia/Muscat', hour12: true, hour: 'numeric', minute: 'numeric' });
      return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()} ${time}`;
    } catch (e) {
      return dateString;
    }
  }, []);

  const formatTransactionDate = useCallback((dateString) => {
    if (!dateString) return '-';
    const s = String(dateString);
    const ymd = s.length >= 10 ? s.slice(0, 10) : s;
    if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
      const [y, m, d] = ymd.split('-');
      return `${parseInt(d, 10)}/${parseInt(m, 10)}/${y}`;
    }
    try {
      const d = new Date(dateString);
      if (Number.isNaN(d.getTime())) return '-';
      return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
    } catch (e) {
      return dateString;
    }
  }, []);

  // Confirmation handlers
  const handleConfirmPayment = async (paymentId, confirm = true) => {
    if (!hasPermission(PERMISSIONS.PAYMENTS_CONFIRM)) {
      setResponseMessage(t('paymentsScreen.noConfirmPermission'));
      setFailedNotification(true);
      return;
    }

    setLoading(true);
    try {
      const response = await confirmPayment(paymentId, confirm);
      const message = response.data?.message
        || (confirm ? t('paymentsScreen.snackbarConfirmed') : t('paymentsScreen.snackbarUnconfirmed'));
      setResponseMessage(message);
      setSuccessNotification(true);
      loadPayments();
    } catch (error) {
      console.error('Failed to confirm payment:', error);
      setResponseMessage(translateAxiosErrorMessage(error, t) || t('paymentsScreen.confirmFailed'));
      setFailedNotification(true);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkConfirm = async (confirm = true) => {
    if (!hasPermission(PERMISSIONS.PAYMENTS_CONFIRM)) {
      setResponseMessage(t('paymentsScreen.noConfirmPermission'));
      setFailedNotification(true);
      return;
    }

    if (selectionModel.length === 0) {
      setResponseMessage(t('paymentsScreen.selectAtLeastOne'));
      setFailedNotification(true);
      return;
    }

    setLoading(true);
    try {
      const response = await bulkConfirmPayments(selectionModel, confirm);
      const data = response.data;
      
      let message = data.message || (confirm
        ? t('paymentsScreen.bulkConfirmedCount', { count: selectionModel.length })
        : t('paymentsScreen.bulkUnconfirmedCount', { count: selectionModel.length }));
      
      if (data.results) {
        const { success, already_confirmed, already_unconfirmed, not_found, errors } = data.results;
        const parts = [];
        
        if (success && success.length > 0) {
          parts.push(confirm
            ? t('paymentsScreen.bulkConfirmedCount', { count: success.length })
            : t('paymentsScreen.bulkUnconfirmedCount', { count: success.length }));
        }
        if (already_confirmed && already_confirmed.length > 0) {
          parts.push(t('paymentsScreen.bulkAlreadyConfirmed', { count: already_confirmed.length }));
        }
        if (already_unconfirmed && already_unconfirmed.length > 0) {
          parts.push(t('paymentsScreen.bulkAlreadyUnconfirmed', { count: already_unconfirmed.length }));
        }
        if (not_found && not_found.length > 0) {
          parts.push(t('paymentsScreen.bulkNotFound', { count: not_found.length }));
        }
        if (errors && errors.length > 0) {
          parts.push(t('paymentsScreen.bulkHadErrors', { count: errors.length }));
        }
        
        if (parts.length > 0) {
          message = parts.join('. ');
        }
      }
      
      setResponseMessage(message);
      setSuccessNotification(true);
      loadPayments();
    } catch (error) {
      console.error('Failed to bulk confirm payments:', error);
      setResponseMessage(translateAxiosErrorMessage(error, t) || t('paymentsScreen.bulkConfirmFailed'));
      setFailedNotification(true);
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(() => [
    { field: 'id', headerName: t('paymentsScreen.colId'), flex: 1, minWidth: 80 },
    { 
      field: 'invoice', 
      headerName: t('paymentsScreen.colInvoice'), 
      flex: 1.5, 
      minWidth: 100,
      renderCell: (params) => params.row.invoice ? `#${params.row.invoice}` : '-'
    },
    {
      field: 'owner',
      headerName: t('paymentsScreen.colCustomer'),
      flex: 2,
      minWidth: 150,
      renderCell: (params) => getLocalizedUserFullName(params.row.owner, i18n.language) || '-',
    },
    { 
      field: 'shipments', 
      headerName: t('paymentsScreen.colShipments'), 
      flex: 2, 
      minWidth: 150,
      renderCell: (params) => {
        const shipments = params.row.shipments || [];
        if (shipments.length === 0) return '-';
        return (
          <Box>
            <Grid container spacing={0.5} sx={{ width: '100%' }}>
              {shipments.map((shipment) => {
                const shipmentId = typeof shipment === 'object' ? shipment.id : shipment;
                return (
                  <Grid item key={shipmentId}>
                    <Chip
                      variant="soft"
                      size="sm"
                      sx={{
                        borderRadius: '4px',
                        fontSize: '0.75rem'
                      }}
                    >
                      #{shipmentId}
                    </Chip>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        );
      }
    },
    { 
      field: 'payment_method', 
      headerName: t('paymentsScreen.colPaymentMethod'), 
      flex: 1.5, 
      minWidth: 130,
      renderCell: (params) => (
        <Chip size="small" variant="soft" color="primary">
          {paymentMethodLabel(params.row.payment_method)}
        </Chip>
      )
    },
    { 
      field: 'amount', 
      headerName: t('paymentsScreen.colAmount'), 
      flex: 1.5, 
      minWidth: 140,
      renderCell: (params) => {
        const amount = parseFloat(params.row.amount || 0);
        const refunded = parseFloat(params.row.refunded_amount || '0');
        const net = amount - refunded;
        const isRefunded = params.row.is_refunded === true;
        return (
          <Box>
            <Typography fontWeight="md" color="success" component="div" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
              <OmrAmount value={net} />
            </Typography>
            {isRefunded && (
              <Typography level="body-xs" color="neutral" sx={{ mt: 0.25, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }} component="div">
                <OmrAmount value={amount} />
                <span aria-hidden>−</span>
                <OmrAmount value={refunded} />
                <span>{t('paymentsScreen.amountNetBreakdownSuffix')}</span>
              </Typography>
            )}
          </Box>
        );
      }
    },
    { 
      field: 'refunded_amount', 
      headerName: t('paymentsScreen.colRefunded'), 
      flex: 1.2, 
      minWidth: 140,
      renderCell: (params) => {
        const refunded = parseFloat(params.row.refunded_amount || '0');
        if (refunded <= 0) return '-';
        return (
          <Typography level="body-sm" color="danger" component="div">
            <OmrAmount value={refunded} />
          </Typography>
        );
      }
    },
    { 
      field: 'is_refunded', 
      headerName: t('paymentsScreen.colRefundStatus'), 
      flex: 1.2, 
      minWidth: 120,
      renderCell: (params) => {
        const isRefunded = params.row.is_refunded === true;
        if (!isRefunded) return '-';
        const amount = parseFloat(params.row.amount || 0);
        const refunded = parseFloat(params.row.refunded_amount || '0');
        const isPartial = refunded > 0 && refunded < amount;
        return (
          <Chip
            size="sm"
            variant="soft"
            color="warning"
            sx={{ fontWeight: 500, fontSize: '0.75rem' }}
          >
            {isPartial ? t('paymentsScreen.refundPartialLabel') : t('paymentsScreen.refundFullLabel')}
          </Chip>
        );
      }
    },
    { 
      field: 'refund_reason', 
      headerName: t('paymentsScreen.colRefundReason'), 
      flex: 2, 
      minWidth: 220,
      renderCell: (params) => {
        const reason = params.row.refund_reason;
        if (!reason) return '-';
        return (
          <Typography level="body-sm" sx={{ maxWidth: '100%', minWidth: 0 }} noWrap title={reason}>
            {reason}
          </Typography>
        );
      }
    },
    { 
      field: 'transaction_reference', 
      headerName: t('paymentsScreen.colTransactionRef'), 
      flex: 2, 
      minWidth: 150,
      renderCell: (params) => params.row.transaction_reference || '-'
    },
    {
      field: 'transaction_date',
      headerName: t('paymentsScreen.colTransactionDate'),
      flex: 1.5,
      minWidth: 130,
      renderCell: (params) => formatTransactionDate(params.row.transaction_date),
    },
    { 
      field: 'account_name', 
      headerName: t('paymentsScreen.colAccountName'), 
      flex: 2, 
      minWidth: 150,
      renderCell: (params) => params.row.account_name || '-'
    },
    { 
      field: 'is_confirmed', 
      headerName: t('paymentsScreen.colStatus'), 
      flex: 1.5, 
      minWidth: 140,
      renderCell: (params) => {
        const isConfirmed = params.row.is_confirmed;
        return (
          <Chip 
            size="sm" 
            variant={isConfirmed ? 'solid' : 'outlined'}
            color={isConfirmed ? 'success' : 'neutral'}
            startDecorator={isConfirmed ? <CheckCircleIcon fontSize="small" /> : <CancelIcon fontSize="small" />}
            sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              ...(isConfirmed ? {
                backgroundColor: '#10b981',
                color: '#ffffff',
              } : {
                borderColor: '#d1d5db',
                color: '#6b7280',
              })
            }}
          >
            {isConfirmed ? t('paymentsScreen.statusConfirmed') : t('paymentsScreen.statusUnconfirmed')}
          </Chip>
        );
      }
    },
    { 
      field: 'confirmed_by_name', 
      headerName: t('paymentsScreen.colConfirmedBy'), 
      flex: 2, 
      minWidth: 150,
      renderCell: (params) => {
        if (!params.row.is_confirmed) return '-';
        return (
          <Typography level="body-sm" sx={{ color: params.row.confirmed_by_name ? 'text.primary' : 'text.tertiary' }}>
            {params.row.confirmed_by_name || '-'}
          </Typography>
        );
      }
    },
    { 
      field: 'confirmed_at', 
      headerName: t('paymentsScreen.colConfirmedAt'), 
      flex: 2, 
      minWidth: 160,
      renderCell: (params) => {
        if (!params.row.is_confirmed || !params.row.confirmed_at) return '-';
        return (
          <Typography level="body-sm" sx={{ color: 'text.primary' }}>
            {formatDate(params.row.confirmed_at)}
          </Typography>
        );
      }
    },
    { 
      field: 'created_at', 
      headerName: t('paymentsScreen.colCreatedAt'), 
      flex: 2, 
      minWidth: 160,
      renderCell: (params) => formatDate(params.row.created_at)
    },
    {
      field: 'actions',
      headerName: t('paymentsScreen.colActions'),
      flex: 2,
      minWidth: 260,
      sortable: false,
      pinned: 'right',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {hasPermission(PERMISSIONS.PAYMENTS_VIEW) && (
            <Tooltip title={t('paymentsScreen.tooltipReceiptPdf')}>
              <IconButton
                size="sm"
                variant="soft"
                color="neutral"
                onClick={(e) => {
                  e.stopPropagation();
                  setReceiptPdfPayment(params.row);
                }}
              >
                <PictureAsPdfIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {hasPermission(PERMISSIONS.PAYMENTS_CONFIRM) && (
            <>
              {params.row.is_confirmed ? (
                <Tooltip title={t('paymentsScreen.tooltipUnconfirm')}>
                  <IconButton
                    size="sm"
                    variant="soft"
                    color="warning"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleConfirmPayment(params.row.id, false);
                    }}
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title={t('paymentsScreen.tooltipConfirm')}>
                  <IconButton
                    size="sm"
                    variant="soft"
                    color="success"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleConfirmPayment(params.row.id, true);
                    }}
                  >
                    <CheckCircleIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
          {hasPermission(PERMISSIONS.PAYMENTS_UPDATE) && (
            <Tooltip title={t('actions.edit')}>
              <IconButton
                size="sm"
                variant="soft"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditClick(params.row);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {hasPermission(PERMISSIONS.PAYMENTS_REFUND) &&
            (parseFloat(params.row.amount || 0) - parseFloat(params.row.refunded_amount || '0')) > 0 && (
            <Tooltip title={t('paymentsScreen.tooltipRefund')}>
              <IconButton
                size="sm"
                variant="soft"
                color="neutral"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRefundClick(params.row);
                }}
              >
                <UndoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {hasPermission(PERMISSIONS.PAYMENTS_DELETE) && (
            <Tooltip title={t('actions.delete')}>
              <IconButton
                size="sm"
                variant="soft"
                color="danger"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(params.row);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )
    },
  ], [
    t,
    i18n.language,
    hasPermission,
    formatDate,
    formatTransactionDate,
    paymentMethodLabel,
    handleConfirmPayment,
    handleEditClick,
    handleRefundClick,
    handleDeleteClick,
    setReceiptPdfPayment,
  ]);

  return (
    <DirectionalMaterialThemeProvider>
      <CssBaseline />
      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex' }} m={3}>
          <Box width={"100%"}>
            <Box sx={{ mb: 3 }}>
              <Breadcrumbs separator={<KeyboardArrowRight />} aria-label="breadcrumbs">
                {[t('paymentsScreen.pageTitle'), t('paymentsScreen.breadcrumbList')].map((item) => (
                  <Link key={item} color="neutral" href="/payments">
                    {item}
                  </Link>
                ))}
              </Breadcrumbs>
              <Typography textAlign={"start"} level="h1">{t('paymentsScreen.pageTitle')}</Typography>
            </Box>

            <PermissionGate permissions={[PERMISSIONS.PAYMENTS_LIST]}>
              {/* Filters */}
              <Grid container spacing={2} sx={{ flexGrow: 1, mb: 2 }}>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <FormLabel>{t('paymentsScreen.searchLabel')}</FormLabel>
                  <Input
                    placeholder={t('paymentsScreen.searchPlaceholder')}
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    startDecorator={<SearchIcon />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <CustomerOptionList
                    label={t('widgets.customer')}
                    defaultValue={filterCustomer}
                    onChange={(event, value) => {
                      setFilterCustomer(value);
                      setFilters(prev => ({ ...prev, customer: value ? String(value.id) : '' }));
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2} lg={2}>
                  <FormLabel>{t('widgets.paymentMethod')}</FormLabel>
                  <Select
                    value={filters.payment_method}
                    onChange={(e, value) => setFilters(prev => ({ ...prev, payment_method: value || '' }))}
                    placeholder={t('widgets.paymentMethod')}
                  >
                    {PAYMENT_METHOD_OPTIONS_WITH_ALL.map((opt) => (
                      <Option key={opt.value || 'all'} value={opt.value}>
                        {paymentMethodLabel(opt.value)}
                      </Option>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={12} sm={6} md={2} lg={2}>
                  <AccountOptionList
                    label={t('widgets.account')}
                    variant="filter"
                    currentAccount={filterAccount}
                    setCurrentAccount={(acc) => {
                      setFilterAccount(acc);
                      setFilters(prev => ({ ...prev, account: acc ? String(acc.id) : '' }));
                    }}
                    showActiveOnly={true}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2} lg={2}>
                  <FormControl error={!!filterErrors.start_date}>
                    <FormLabel>{t('paymentsScreen.createdAtStart')}</FormLabel>
                    <Input
                      type="date"
                      value={filters.start_date}
                      onChange={(e) => {
                        setFilters(prev => ({ ...prev, start_date: e.target.value }));
                        // Clear error when user changes the date
                        if (filterErrors.start_date) {
                          setFilterErrors(prev => ({ ...prev, start_date: '' }));
                        }
                      }}
                      slotProps={{ input: { placeholder: t('paymentsScreen.createdAtStart') } }}
                      error={!!filterErrors.start_date}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2} lg={2}>
                  <FormControl error={!!filterErrors.end_date}>
                    <FormLabel>{t('paymentsScreen.createdAtEnd')}</FormLabel>
                    <Input
                      type="date"
                      value={filters.end_date}
                      onChange={(e) => {
                        setFilters(prev => ({ ...prev, end_date: e.target.value }));
                        // Clear error when user changes the date
                        if (filterErrors.end_date) {
                          setFilterErrors(prev => ({ ...prev, end_date: '' }));
                        }
                      }}
                      slotProps={{ input: { placeholder: t('paymentsScreen.createdAtEnd') } }}
                      error={!!filterErrors.end_date}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={3}>
                  <FormLabel>{t('paymentsScreen.confirmationStatus')}</FormLabel>
                  <Select
                    value={filters.is_confirmed}
                    onChange={(e, value) => setFilters(prev => ({ ...prev, is_confirmed: value || '' }))}
                    placeholder={t('paymentsScreen.confirmationStatus')}
                  >
                    <Option value="">{t('paymentsScreen.allStatus')}</Option>
                    <Option value="true">{t('paymentsScreen.statusConfirmed')}</Option>
                    <Option value="false">{t('paymentsScreen.statusUnconfirmed')}</Option>
                  </Select>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={3}>
                  <FormLabel>{t('paymentsScreen.refundStatusFilter')}</FormLabel>
                  <Select
                    value={filters.is_refunded}
                    onChange={(e, value) => setFilters(prev => ({ ...prev, is_refunded: value || '' }))}
                    placeholder={t('paymentsScreen.refundStatusFilter')}
                  >
                    <Option value="">{t('paymentsScreen.filterAll')}</Option>
                    <Option value="true">{t('paymentsScreen.filterRefunded')}</Option>
                    <Option value="false">{t('paymentsScreen.filterNotRefunded')}</Option>
                  </Select>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={3}>
                  <FormControl error={!!filterErrors.confirmed_at_start}>
                    <FormLabel>{t('paymentsScreen.confirmedAtStart')}</FormLabel>
                    <Input
                      type="date"
                      value={filters.confirmed_at_start}
                      onChange={(e) => {
                        setFilters(prev => ({ ...prev, confirmed_at_start: e.target.value }));
                        // Clear error when user changes the date
                        if (filterErrors.confirmed_at_start) {
                          setFilterErrors(prev => ({ ...prev, confirmed_at_start: '' }));
                        }
                      }}
                      slotProps={{ input: { placeholder: t('paymentsScreen.confirmedAtStart') } }}
                      error={!!filterErrors.confirmed_at_start}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={3}>
                  <FormControl error={!!filterErrors.confirmed_at_end}>
                    <FormLabel>{t('paymentsScreen.confirmedAtEnd')}</FormLabel>
                    <Input
                      type="date"
                      value={filters.confirmed_at_end}
                      onChange={(e) => {
                        setFilters(prev => ({ ...prev, confirmed_at_end: e.target.value }));
                        // Clear error when user changes the date
                        if (filterErrors.confirmed_at_end) {
                          setFilterErrors(prev => ({ ...prev, confirmed_at_end: '' }));
                        }
                      }}
                      slotProps={{ input: { placeholder: t('paymentsScreen.confirmedAtEnd') } }}
                      error={!!filterErrors.confirmed_at_end}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={1} lg={1}>
                  <FormLabel sx={{ visibility: 'hidden' }}>{t('paymentsScreen.filterSearchHidden')}</FormLabel>
                  <Button type="submit" loading={loading} startDecorator={<SearchIcon />} fullWidth>
                    {t('actions.search')}
                  </Button>
                </Grid>
              </Grid>

              {/* Bulk Actions */}
              {hasPermission(PERMISSIONS.PAYMENTS_CONFIRM) && selectionModel.length > 0 && (
                <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Typography level="body-sm" sx={{ mr: 1 }}>
                    {t('paymentsScreen.bulkSelected', { count: selectionModel.length })}
                  </Typography>
                  <Button
                    variant="soft"
                    color="success"
                    size="sm"
                    onClick={() => handleBulkConfirm(true)}
                    loading={loading}
                    startDecorator={<CheckCircleIcon />}
                  >
                    {t('paymentsScreen.confirmSelected')}
                  </Button>
                  <Button
                    variant="soft"
                    color="warning"
                    size="sm"
                    onClick={() => handleBulkConfirm(false)}
                    loading={loading}
                    startDecorator={<CancelIcon />}
                  >
                    {t('paymentsScreen.unconfirmSelected')}
                  </Button>
                </Box>
              )}

              {/* Table */}
              <Box>
                <DataGrid
                  slots={dataGridNativeCheckboxSlots}
                  checkboxSelection={hasPermission(PERMISSIONS.PAYMENTS_CONFIRM)}
                  onRowSelectionModelChange={(newSelection) => {
                    setSelectionModel(newSelection);
                  }}
                  selectionModel={selectionModel}
                  rows={payments || []}
                  columns={columns}
                  loading={loading}
                  pagination
                  paginationModel={paginationModel}
                  paginationMode="server"
                  rowCount={data ? data.count : 0}
                  onPaginationModelChange={(params) => {
                    if (params.pageSize !== paginationModel.pageSize) {
                      handlePageSizeChange(params.pageSize);
                    } else {
                      handlePageChange(params.page);
                    }
                  }}
                  pageSizeOptions={[10, 25, 50, 100]}
                  hideFooterSelectedRowCount
                  getRowHeight={() => 'auto'}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    '& .MuiDataGrid-root': {
                      border: 'none',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#f5f5f5',
                      borderBottom: '2px solid #e0e0e0',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#333',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      '& .MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 600,
                        fontSize: '14px',
                        color: '#333',
                      },
                    },
                    '& .MuiDataGrid-cell': {
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                      lineHeight: '1.5',
                      padding: '12px 16px',
                      fontSize: '14px',
                      color: '#555',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      borderBottom: '1px solid #f0f0f0',
                    },
                    '& .MuiDataGrid-row': {
                      '&:hover': {
                        backgroundColor: '#f8f9fa',
                        cursor: 'pointer',
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#e3f2fd',
                        '&:hover': {
                          backgroundColor: '#bbdefb',
                        },
                      },
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: '2px solid #e0e0e0',
                      backgroundColor: '#fafafa',
                      fontSize: '14px',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    },
                    '& .MuiDataGrid-toolbarContainer': {
                      padding: '12px',
                      backgroundColor: '#fafafa',
                      borderBottom: '1px solid #e0e0e0',
                    },
                    '@media (max-width: 600px)': {
                      '& .MuiDataGrid-toolbarContainer': {
                        flexDirection: 'column',
                        gap: 1,
                      },
                      '& .MuiDataGrid-footer': {
                        flexDirection: 'column',
                        gap: 1,
                      },
                      '& .MuiDataGrid-cell': {
                        minHeight: '60px',
                        alignItems: 'center',
                        padding: '8px',
                        fontSize: '13px',
                      },
                      '& .MuiDataGrid-columnHeaders': {
                        fontSize: '13px',
                      },
                    }
                  }}
                />
              </Box>
            </PermissionGate>

            {/* Notifications */}
            <Snackbar
              autoHideDuration={6000}
              open={failedNotification}
              variant="soft"
              color="danger"
              onClose={() => setFailedNotification(false)}
            >
              {responseMessage}
            </Snackbar>
            <Snackbar
              autoHideDuration={3000}
              open={successNotification}
              variant="soft"
              color="success"
              onClose={() => setSuccessNotification(false)}
            >
              {responseMessage}
            </Snackbar>

            {/* Edit Modal */}
            <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)}>
              <ModalDialog
                sx={{
                  maxWidth: { xs: '90vw', sm: '500px', md: '600px' },
                  width: '100%',
                  maxHeight: { xs: '90vh', sm: 'auto' },
                  overflow: 'auto',
                }}
              >
                <ModalClose />
                <DialogTitle>{t('paymentsScreen.modalEditTitle')}</DialogTitle>
                <DialogContent>
                  <Stack spacing={2} sx={{ mt: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <FormLabel>{t('widgets.paymentMethod')}</FormLabel>
                          <Select
                            value={editForm.payment_method}
                            onChange={(e, value) => setEditForm(prev => ({ ...prev, payment_method: value || '' }))}
                          >
                            {PAYMENT_METHOD_OPTIONS.map((opt) => (
                              <Option key={opt.value} value={opt.value}>
                                {paymentMethodLabel(opt.value)}
                              </Option>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth error={!!editFormErrors.amount}>
                          <FormLabel>{t('paymentsScreen.colAmount')}</FormLabel>
                          <Input
                            type="number"
                            value={editForm.amount}
                            onChange={(e) => {
                              setEditForm(prev => ({ ...prev, amount: e.target.value }));
                              // Clear error when user starts typing
                              if (editFormErrors.amount) {
                                setEditFormErrors({ amount: '' });
                              }
                            }}
                            slotProps={{ input: { step: '0.001', min: '0.001' } }}
                            error={!!editFormErrors.amount}
                          />
                          {editFormErrors.amount && (
                            <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>
                              {editFormErrors.amount}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <FormLabel>{t('paymentsScreen.colTransactionRef')}</FormLabel>
                          <Input
                            value={editForm.transaction_reference}
                            onChange={(e) => setEditForm(prev => ({ ...prev, transaction_reference: e.target.value }))}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ width: '100%' }}>
                          <AccountOptionList
                            label={t('widgets.account')}
                            currentAccount={editForm.account}
                            setCurrentAccount={(acc) => setEditForm(prev => ({ ...prev, account: acc || null }))}
                            showActiveOnly={true}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Stack>
                </DialogContent>
                <DialogActions
                  sx={{
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 0 },
                    '& > button': {
                      width: { xs: '100%', sm: 'auto' },
                    },
                  }}
                >
                  <Button variant="plain" color="neutral" onClick={() => setEditModalOpen(false)}>
                    {t('actions.cancel')}
                  </Button>
                  <Button onClick={handleEditSubmit} loading={loading}>
                    {t('actions.save')}
                  </Button>
                </DialogActions>
              </ModalDialog>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
              <ModalDialog variant="outlined" role="alertdialog">
                <DialogTitle>
                  <WarningRounded />
                  {t('paymentsScreen.modalConfirmDeleteTitle')}
                </DialogTitle>
                <Divider />
                <DialogContent>
                  {deletingPayment && t('paymentsScreen.modalDeleteBody', { id: deletingPayment.id })}
                </DialogContent>
                <DialogActions>
                  <Button variant="plain" color="neutral" onClick={() => setDeleteModalOpen(false)}>
                    {t('actions.cancel')}
                  </Button>
                  <Button variant="solid" color="danger" onClick={handleDeleteConfirm} loading={loading}>
                    {t('actions.delete')}
                  </Button>
                </DialogActions>
              </ModalDialog>
            </Modal>

            <Modal open={!!receiptPdfPayment} onClose={() => setReceiptPdfPayment(null)}>
              <ModalDialog
                sx={{
                  maxWidth: 960,
                  width: '100%',
                  maxHeight: '95vh',
                  overflow: 'auto',
                  p: 2,
                }}
              >
                <ModalClose />
                <DialogTitle>{t('paymentsScreen.receiptModalTitle')}</DialogTitle>
                <DialogContent sx={{ px: 0 }}>
                  {receiptPdfPayment ? <PaymentReceipt paymentData={receiptPdfPayment} /> : null}
                </DialogContent>
              </ModalDialog>
            </Modal>

            {/* Refund Modal */}
            <Modal open={refundModalOpen} onClose={() => setRefundModalOpen(false)}>
              <ModalDialog
                variant="outlined"
                role="alertdialog"
                sx={{
                  maxWidth: { xs: '90vw', sm: '420px' },
                  width: '100%',
                }}
              >
                <DialogTitle>
                  <UndoIcon />
                  {t('paymentsScreen.modalRefundTitle')}
                </DialogTitle>
                <Divider />
                <DialogContent>
                  {refundingPayment && (() => {
                    const totalAmount = parseFloat(refundingPayment.amount || 0);
                    const alreadyRefunded = parseFloat(refundingPayment.refunded_amount || '0');
                    const refundableAmount = totalAmount - alreadyRefunded;
                    const methodLabel = paymentMethodLabel(refundingPayment.payment_method);
                    return (
                    <Stack spacing={2} sx={{ mt: 1 }}>
                      <Typography level="body-sm" component="div">
                        {t('paymentsScreen.refundBodyIntroBeforeAmount', {
                          id: refundingPayment.id,
                          method: methodLabel,
                        })}
                        <OmrAmount value={refundableAmount} />
                        {t('paymentsScreen.refundBodyIntroAfterAmount', {
                          id: refundingPayment.id,
                          method: methodLabel,
                        })}
                        {alreadyRefunded > 0 ? (
                          <>
                            {' '}
                            {t('paymentsScreen.refundAlreadyPartialBeforeAmount')}
                            <OmrAmount value={alreadyRefunded} />
                            {t('paymentsScreen.refundAlreadyPartialAfterAmount')}
                          </>
                        ) : null}
                        .
                      </Typography>
                      <FormControl>
                        <FormLabel>{t('paymentsScreen.reasonOptional')}</FormLabel>
                        <Input
                          value={refundReason}
                          onChange={(e) => setRefundReason(e.target.value)}
                        />
                      </FormControl>
                    </Stack>
                    );
                  })()}
                </DialogContent>
                <DialogActions>
                  <Button variant="plain" color="neutral" onClick={() => setRefundModalOpen(false)}>
                    {t('actions.cancel')}
                  </Button>
                  <Button variant="solid" color="neutral" onClick={handleRefundConfirm} loading={loading} startDecorator={<UndoIcon />}>
                    {t('paymentsScreen.refundButton')}
                  </Button>
                </DialogActions>
              </ModalDialog>
            </Modal>
          </Box>
        </Box>
      </form>
    </DirectionalMaterialThemeProvider>
  );
}

